<template>
  <div class="profile-card text-center">
    <!-- Show the avatar/profile picture -->
    <v-avatar size="160">
      <img :src="proxyUrl(userData.data.user_profile.picture)" />
    </v-avatar>

    <!-- Name and blue-tick for verification -->
    <div class="text-h6 font-weight-bold mt-3">
      <span>
        {{ userData.data.user_profile.fullname }}
      </span>

      <v-icon
        v-if="userData.data.user_profile.is_verified"
        color="blue"
      >verified</v-icon>
    </div>

    <!-- Profile caption/bio text -->
    <p class="mt-1 text-body-2">
      {{ userData.data.user_profile.description }}
    </p>

    <!-- Show the number of followers and redirect it to their web link on click -->
    <div class="platform-cta text-left mx-auto">
      <v-list-item
        ripple
        @click="redirectToWeb(userData.data.user_profile.url)"
      >
        <v-list-item-action class="mr-3">
          <v-img
            height="36"
            width="36"
            :src="platformIconMap[userData.platform]"
          />
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            <div class="font-weight-medium platform-name text-capitalize">
              {{ userData.platform }}
            </div>
            <div class="text-gray-custom text-caption">
              {{ nFormatter(userData.data.user_profile.followers) }} Followers
            </div>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </div>

    <!-- pricing and contact list items -->
    <v-list
      dense
      class="mt-3 text-left transparent"
    >
      <!-- For Pricing Section -->
      <!-- <v-list-item ripple>
        <v-list-item-icon class="mr-3">
          <v-icon color="black">money</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>Pricing</v-list-item-title>
        </v-list-item-content>
      </v-list-item> -->

      <!-- For Contact Links -->
      <v-list-item
        v-if="contactMethods"
        ripple
        two-line
        @click="shouldShowContactDialog = true"
      >
        <v-list-item-icon class="mr-3">
          <v-icon class="mt-3" color="black">connect_without_contact</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>Contact</v-list-item-title>
          <v-list-item-subtitle>
            {{ contactMethods.length }} ways
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <!-- Ask whether or not to send email -->
    <div class="text-center">
      <v-checkbox
        v-model="shouldSendEmail"
        label="Send Email"
        hide-details
        class="d-inline-block d-md-block"
      ></v-checkbox>
    </div>

    <!-- CTA to download the PDF generated form backend -->
    <v-btn
      color="primary"
      large
      depressed
      class="mt-2"
      :loading="isDownloading"
      :disabled="isDownloading"
      @click="triggerDownload"
      max-width="100%"
      :class="{ 'shadow--primary': !isDownloading }"
    >
      Download Report
    </v-btn>

    <div
      v-if="isDownloading"
      class="mx-auto progress-loader"
    >
      <v-progress-linear
        :value="downloadProgress"
      />
    </div>

    <!-- Create dialog elements to be shown -->
    <!-- Dialog for contact methods -->
    <v-dialog
      v-model="shouldShowContactDialog"
      width="500"
    >
      <v-card>
        <v-card-title>
          Contacts
        </v-card-title>

        <v-card-text v-if="contactMethods">
          <v-list dense>
            <v-list-item
              v-for="(item, index) in contactMethods"
              :key="index"
              ripple
              two-line
              :href="item.formatted_value"
              class="align-center"
              @click.prevent="redirectToWeb(item.formatted_value)"
            >
              <v-list-item-icon class="mr-4">
                <v-icon class="mt-3" color="black">
                  {{ item.type === "email" ? "email" : "public" }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.value }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.formatted_value }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            text
            color="primary"
            @click="shouldShowContactDialog = false"
          >
            Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Export the SFC
export default {
  // Name of the component
  name: "ProfileCard",

  // Accept incoming data from parent
  props: {
    username: String,
    platform: String,

    accountId: {
      type: [String, Number],
      required: false,
      default: null
    },

    userData: {
      type: Object,
      required: true
    }
  },

  // Define local data variables
  data: () => ({
    // The checkbox input
    shouldSendEmail: false,
    // The contact options CTA
    shouldShowContactDialog: false,
    // Current profile's download progress
    downloadProgress: 0,
    // The interval handle
    progressHandle: null,
    // The triggerId to compare with to hide the progress
    triggerId: null
  }),

  // Local readonly computable variables
  computed: {
    /**
     * Whether or not to show the indefinite progress bar
     *
     * @returns {Boolean}
     */
    isDownloading() {
      return this.downloadProgress > 0 && this.downloadProgress < 100
    },

    /**
     * Get the contact options for this profile
     *
     * @returns {Array}
     */
    contactMethods() {
      return this.userData.data.user_profile.contacts
    }
  },

  // Define method functions
  methods: {
    /**
     * Update the value for progress bar
     *
     * @returns {void}
     */
    moveProgress() {
      if (this.downloadProgress >= 90) {
        window.clearInterval(this.progressHandle)
      }
      else {
        this.downloadProgress += 6
      }
    },

    /**
     * Trigger download network request
     *
     * @returns {void}
     */
    triggerDownload() {
      // Reset the download progress
      this.downloadProgress = 1
      this.triggerId = String(Date.now())

      // Trigger the Vuex action to make network request
      this.$store.dispatch("userProfile/downloadProfile", {
        platform: this.platform,
        username: this.username,
        accountId: this.accountId,
        triggerId: this.triggerId,
        shouldSendEmail: this.shouldSendEmail
      })

      // Every 1 second, move the progress bar a bit more
      this.progressHandle = window.setInterval(this.moveProgress, 600)
    },

    /**
     * Stop the download progress
     *
     * @param {Event} event
     * @returns {void}
     */
    stopDownloadProgressBar(event) {
      console.log(event)
      console.log(event.detail)

      // If the triggerId matches
      if (event.detail.data.misc.triggerId === this.triggerId) {
        // Reset the download progress
        this.downloadProgress = 0
        window.clearInterval(this.progressHandle)
      }
    }
  },

  /**
   * As soon as the component's data is ready
   *
   * @returns {void}
   */
  created() {
    // Keep listening for a download event
    window.addEventListener("ReadyToDownload", this.stopDownloadProgressBar)
  },

  /**
   * Before this component is about to be removed
   *
   * @returns {void}
   */
  beforeDestroy() {
    // De-register the event listener
    window.removeEventListener("ReadyToDownload", this.stopDownloadProgressBar)
  }
}
</script>

<style lang="stylus" scoped>
.platform-cta
  max-width 20em

  &.is-pdf-view
    max-width 200px

  .v-list-item
    border 1px solid rgba(145, 158, 171, 0.32)
    border-radius 0.5em
    overflow hidden

.progress-loader
  width 100%
  max-width 200px
  border-radius 0 0 16px 16px
  overflow hidden
  margin-top -1px
</style>
